import { getOutgoers } from '@xyflow/react';
import { TARGET_HANDLE_ID, DEFAULT_SOURCE_HANDLE_ID } from './handles';

export function createsCycle({ source, target, nodes, edges }) {
    if (source === target) return true;
    const structural = edges.filter(e => e.type === 'pathEdge');
    const targetNode = nodes.find(n => n.id === target);
    if (!targetNode) return false;

    const seen = new Set();
    const walk = (node) => {
        for (const out of getOutgoers(node, nodes, structural)) {
            if (out.id === source) return true;
            if (seen.has(out.id)) continue;
            seen.add(out.id);
            if (walk(out)) return true;
        }
        return false;
    };
    return walk(targetNode);
}

export function validateConnection({ connection, nodes, edges, ignoreEdgeID }) {
    const { source, target, sourceHandle, targetHandle } = connection;
    if (!source || !target) return { ok: false };
    if (source === target) return { ok: false, reason: 'A step cannot connect to itself.' };

    const sourceNode = nodes.find(n => n.id === source);
    const targetNode = nodes.find(n => n.id === target);
    if (!sourceNode || !targetNode) return { ok: false };
    if (targetNode.type !== 'step') return { ok: false };
    if (sourceNode.type !== 'step' && sourceNode.type !== 'trigger') return { ok: false };
    if (targetHandle && targetHandle !== TARGET_HANDLE_ID) return { ok: false };

    const slot = sourceHandle || DEFAULT_SOURCE_HANDLE_ID;
    const occupied = edges.find(e => e.type === 'pathEdge' && e.source === source
        && (e.sourceHandle || DEFAULT_SOURCE_HANDLE_ID) === slot
        && e.data?.pathEdgeID !== ignoreEdgeID);
    if (occupied) return { ok: false, reason: 'That output already goes somewhere. Drag the existing connection instead.' };

    const rest = edges.filter(e => e.data?.pathEdgeID !== ignoreEdgeID);
    if (createsCycle({ source, target, nodes, edges: rest })) {
        return { ok: false, reason: 'That would create a loop. Paths must always move forward.' };
    }

    return { ok: true };
}

export function validateMove({ edgeID, source, target, stepID, nodes, edges }) {
    const structural = edges.filter(e =>
        e.type === 'pathEdge' &&
        e.id !== edgeID &&
        e.source !== stepID &&
        e.target !== stepID
    );
    // A -> S and S -> B must not create cycles after S's old edges are removed.
    const aToSCycles = createsCycle({ source, target: stepID, nodes, edges: structural });
    const sToBCycles = createsCycle({ source: stepID, target, nodes, edges: structural });
    return !aToSCycles && !sToBCycles;
}
